import * as HoverCard from "@radix-ui/react-hover-card";
import classNames from "classnames";
import { createContext, forwardRef, useCallback, useContext, useState } from "react";
import { bgColorForStep } from "~/components/Playground/Node/colors";
import { BaseStep } from "~/lib/pipeline/Step";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { twMerge } from "tailwind-merge";
import { StepDependencyContext } from "~/components/build/Show/lib/types";
export { Trigger } from "@radix-ui/react-hover-card";

interface Props extends Pick<BaseStep, "state" | "outcome"> {
  children: React.ReactNode;
  noBorder?: boolean;
  missingDependencies: StepDependencyContext["missing_dependencies"];
}

/**
 * Context to track hover card open state.
 * Used to lazy-mount Portal/Content only when open, reducing memory usage
 * in Firefox/Safari where 200+ mounted portals cause severe performance issues.
 */
const HoverCardOpenContext = createContext<boolean>(false);

export const Root = ({ children }: React.ComponentProps<typeof HoverCard.Root>) => {
  const [open, setOpen] = useState(false);

  return (
    <HoverCard.Root openDelay={750} closeDelay={75} open={open} onOpenChange={setOpen}>
      <HoverCardOpenContext.Provider value={open}>{children}</HoverCardOpenContext.Provider>
    </HoverCard.Root>
  );
};

/**
 * Lazy-mounting Portal wrapper.
 * Only renders children when the hover card is open, preventing 200+ portals
 * from being mounted simultaneously which causes memory issues in Firefox/Safari.
 */
export const Portal = ({ children, ...props }: React.ComponentProps<typeof HoverCard.Portal>) => {
  const isOpen = useContext(HoverCardOpenContext);

  if (!isOpen) {
    return null;
  }

  return <HoverCard.Portal {...props}>{children}</HoverCard.Portal>;
};

export const Content = forwardRef<
  React.ElementRef<typeof HoverCard.Content>,
  React.ComponentProps<typeof HoverCard.Content> & Props & { noBorder?: boolean }
>(
  (
    {
      state,
      outcome,
      missingDependencies,
      children,
      noBorder,
      collisionBoundary,
      collisionPadding,
      className,
      side = "top",
    },
    ref,
  ) => {
    const { build } = useBuild({ strict: false });

    const preventSelection = useCallback((event: React.MouseEvent) => event.stopPropagation(), []);

    return (
      <HoverCard.Content
        ref={ref}
        side={side}
        sideOffset={20}
        onClick={preventSelection}
        className={twMerge(
          classNames(
            "rounded-2 depth-800 z-30 w-96 p-3",
            "data-[state=open]:animate-[slideUp_100ms_cubic-bezier(0.16,1,0.3,1)_forwards]",
            "data-[state=closed]:animate-[slideDown_25ms_linear_forwards]",
            {
              "border border-t-4": !noBorder,
            },
            bgColorForStep({ state, outcome }, missingDependencies, build),
            "bg-white",
          ),
          className,
        )}
        collisionBoundary={collisionBoundary}
        collisionPadding={collisionPadding}
      >
        {children}
      </HoverCard.Content>
    );
  },
);
